import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orgs } from '@/api/catalogs';
import {
  retailOnlineStoreOrders,
  type OnlineOrderStatus,
  type OnlineStoreOrderListItem,
} from '@/api/retailOnlineStoreOrders';
import { usePermissionsByOrg } from '@/hooks/usePermissionsByOrg';
import HintBadge from '@/components/HintBadge';
import DataTable, { type ColumnDef } from '@/shared/components/DataTable';
import { pickDefaultOrg } from '@/store/defaults';

const PERMISSION = 'retail.sales_channel.manage';

const STATUS_LABEL: Record<string, string> = {
  new: 'Новий',
  confirmed: 'Підтверджений',
  preparing: 'Готується',
  shipped: 'Відправлений',
  delivered: 'Доставлений',
  cancelled: 'Скасований',
  returned: 'Повернений',
};

export default function OnlineStoreOrdersPage() {
  const navigate = useNavigate();
  const orgList = useQuery({ queryKey: ['orgs'], queryFn: () => orgs.list({ limit: 200 }) });
  const perms = usePermissionsByOrg(PERMISSION);
  const allowedOrgIds = useMemo(
    () => new Set(perms.availableOrgs.map((o) => o.id)),
    [perms.availableOrgs],
  );

  const [orgId, setOrgId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  useEffect(() => {
    const first = pickDefaultOrg(orgList.data?.items);
    if (!orgId && first) setOrgId(first.id);
  }, [orgList.data, orgId]);

  const list = useQuery({
    queryKey: ['retail-online-store-orders', orgId, statusFilter],
    queryFn: () =>
      retailOnlineStoreOrders.list({
        organization_id: orgId || undefined,
        order_status: statusFilter ? (statusFilter as OnlineOrderStatus) : undefined,
        limit: 500,
      }),
    enabled: !!orgId,
  });

  const cols = useMemo<ColumnDef<OnlineStoreOrderListItem>[]>(
    () => [
      {
        key: 'document_number',
        header: 'Номер',
        width: 150,
        sortValue: (r) => r.document_number ?? '',
        cell: (r) => r.document_number,
      },
      {
        key: 'document_date',
        header: 'Дата',
        width: 170,
        sortValue: (r) => r.document_date ?? '',
        cell: (r) => (r.document_date ? new Date(r.document_date).toLocaleString('uk-UA') : ''),
      },
      {
        key: 'customer_name',
        header: 'Покупець',
        sortValue: (r) => r.customer_name ?? '',
        cell: (r) => r.customer_name,
      },
      {
        key: 'customer_phone',
        header: 'Телефон',
        width: 160,
        sortValue: (r) => r.customer_phone ?? '',
        cell: (r) => r.customer_phone,
      },
      {
        key: 'total_amount',
        header: 'Сума',
        width: 130,
        align: 'right',
        sortValue: (r) => Number(r.total_amount),
        cell: (r) =>
          r.total_amount
            ? Number(r.total_amount).toLocaleString('uk-UA', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
            : '',
      },
      {
        key: 'order_status',
        header: 'Статус',
        width: 160,
        sortValue: (r) => STATUS_LABEL[r.order_status] ?? r.order_status,
        cell: (r) => STATUS_LABEL[r.order_status] ?? r.order_status,
      },
      {
        key: 'fulfilled_retail_sale_id',
        header: 'Виконано',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.fulfilled_retail_sale_id ? 1 : 0),
        cell: (r) => (r.fulfilled_retail_sale_id ? '✓' : '—'),
      },
      {
        key: 'posted',
        header: 'Проведено',
        width: 130,
        align: 'center',
        sortValue: (r) => (r.posted ? 1 : 0),
        cell: (r) => (r.posted ? '✓ Проведено' : 'Чернетка'),
      },
    ],
    [],
  );

  const canCreate = !!orgId && allowedOrgIds.has(orgId);
  const tooltipNew = !orgId
    ? 'Спершу оберіть юрособу'
    : !canCreate
      ? `Немає прав створювати замовлення (${PERMISSION}). У вас є право у ${perms.availableOrgs.length} з ${perms.totalAccessibleOrgs} юросіб`
      : undefined;

  return (
    <>
      <div className="toolbar">
        <h2 style={{ margin: 0, display: 'inline-flex', alignItems: 'center', gap: '0.4rem' }}>
          Замовлення з інтернет-магазину
          <HintBadge
            title="Що це?"
            text={
              'Документ замовлення з онлайн-каналу: інтернет-магазин, Розетка, Prom тощо.\n\n' +
              'Включає:\n' +
              '• Дані покупця (ПIБ, телефон, email, адреса доставки).\n' +
              '• Позиції замовлення + сума доставки.\n' +
              '• Прив\'язку до каналу продажу + опц. до картки клієнта.\n\n' +
              'Lifecycle (FSM статусів):\n' +
              'new → confirmed → preparing → shipped → delivered\n' +
              '   ↓ (на будь-якому етапі)\n' +
              'cancelled / returned\n\n' +
              'У фазі 1 — заглушка без проводок. Реальна інтеграція з маркетплейсами ' +
              '(автоматичне отримання замовлень + sync статусів) — у фазі 2.\n\n' +
              'Зараз можна вручну створити замовлення і відслідковувати його стан до виконання.'
            }
          />
        </h2>
        <select
          value={orgId}
          onChange={(e) => setOrgId(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          {orgList.data?.items.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name} — {o.code}
            </option>
          ))}
        </select>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ width: 140, flex: '0 0 auto' }}
        >
          <option value="">Всі статуси</option>
          <option value="new">Новий</option>
          <option value="confirmed">Підтверджений</option>
          <option value="preparing">Готується</option>
          <option value="shipped">Відправлений</option>
          <option value="delivered">Доставлений</option>
          <option value="cancelled">Скасований</option>
          <option value="returned">Повернений</option>
        </select>
        <button
          className="primary"
          disabled={!canCreate}
          title={tooltipNew}
          style={{ marginLeft: 'auto' }}
          onClick={() => navigate('/retail/online-store-orders/new')}
        >
          + Нове замовлення
        </button>
      </div>
      <DataTable<OnlineStoreOrderListItem>
        rows={list.data?.items ?? []}
        columns={cols}
        rowKey={(r) => r.id}
        loading={list.isLoading}
        emptyText="Немає замовлень."
        onRowDoubleClick={(r) => navigate(`/retail/online-store-orders/${r.id}`)}
        rowMuted={(r) => (r.deletion_mark ? 0.4 : undefined)}
        initialSortKey="document_date"
        initialSortDir="desc"
      />
    </>
  );
}
